import { Drawer, DrawerAction, DrawerBody, DrawerHeader } from '@/components/Drawer';
import { useOrgContext } from '@/contexts/orgContext';
import { useWorkspaceContext } from '@/contexts/workspaceContext';
import { axiosController } from '@/lib/network';
import { Result } from '@/lib/type';
import { getErrorMessage } from '@/utils/utils';
import { Button } from '@tigergraph/app-ui-lib/button';
import { Input } from '@tigergraph/app-ui-lib/input';
import { useStyletron } from '@tigergraph/app-ui-lib/Theme';
import { Select } from '@tigergraph/app-ui-lib/select';
import { StatefulPopover as StatefulToolTipsPopover } from '@tigergraph/app-ui-lib/popover';
import { ACCESSIBILITY_TYPE, PLACEMENT, TRIGGER_TYPE } from 'baseui/popover';
import { Textarea } from 'baseui/textarea';
import { SIZE } from 'baseui/input';
import { Option } from 'baseui/select';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useMutation } from 'react-query';
import { AxiosError } from 'axios';
import { showToast } from '@/components/styledToasterContainer';
import { FormSection } from '@/components/styled';
import { SupportHeaderChatIcon, SupportUploadIllustrationIcon } from '@/pages/home/icons';
import { openZendeskSupportInNewTab } from '@/pages/home/support/zendesk';
import { MdClose } from 'react-icons/md';

/** Single-line controls: same size as Base Web `Select` default (matches Subject to Category / Workspace). */
const FIELD_SIZE = SIZE.default;

const MAX_SUPPORT_IMAGE_ATTACHMENTS = 5;
const MAX_SUPPORT_IMAGE_BYTES = 5 * 1024 * 1024;
const MAX_SUPPORT_IMAGE_MB = 5;

interface SupportDrawerProps {
  isShow: boolean;
  onClose: () => void;
}

type SupportImagePayload = {
  filename: string;
  content_type: string;
  data: string; // base64, no data-url prefix
};

type SupportRequestPayload = {
  org_id: string;
  email_id: string;
  category: string;
  workspace?: string[];
  subject: string;
  description: string;
  images?: SupportImagePayload[];
};

type SupportResponseResult = {
  redirectUrl?: string;
  message?: string;
};

type SupportResponse = Result<SupportResponseResult>;

type SupportFormValues = {
  category: string;
  workspace: string[];
  subject: string;
  description: string;
};

const categoryOptions: Option[] = [
  { id: 'Workspace', label: 'Workspace' },
  { id: 'Database', label: 'Database' },
  { id: 'Load data', label: 'Load data' },
  { id: 'Query Editor', label: 'Query Editor' },
  { id: 'Explore graph', label: 'Explore graph' },
  { id: 'Solutions', label: 'Solutions' },
  { id: 'Add ons', label: 'Add ons' },
  { id: 'Admin', label: 'Admin' },
  { id: 'Other', label: 'Other' },
];

function useMutationSupport() {
  return useMutation<SupportResponse, AxiosError, SupportRequestPayload>(async (payload) => {
    const res = await axiosController.post('/v2/support', payload);
    return res.data;
  });
}

function fileToBase64Payload(file: File): Promise<SupportImagePayload> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Failed to read attachment'));
    reader.onload = () => {
      const result = String(reader.result || '');
      const base64 = result.includes(',') ? result.split(',')[1] : result;
      resolve({
        filename: file.name,
        content_type: file.type || 'application/octet-stream',
        data: base64,
      });
    };
    reader.readAsDataURL(file);
  });
}

export default function SupportDrawer({ isShow, onClose }: SupportDrawerProps) {
  const [css, theme] = useStyletron();
  const { currentOrg, userInfo } = useOrgContext();
  const { workspaces } = useWorkspaceContext();

  /**
   * Match `@tigergraph/app-ui-lib/select`: padding + 14px/16px type.
   * Select placeholder uses Base Web `colors.inputPlaceholder` (see baseui/select StyledPlaceholder), not `text.secondary`.
   */
  const alignWithSelectControlOverrides = useMemo(
    () => ({
      Input: {
        style: {
          paddingLeft: '12px',
          paddingRight: '12px',
          fontSize: '14px',
          lineHeight: '16px',
          color: theme.colors['text.primary'],
          '::-webkit-input-placeholder': {
            fontSize: '14px',
            lineHeight: '16px',
            color: theme.colors.inputPlaceholder,
          },
          '::placeholder': {
            fontSize: '14px',
            lineHeight: '16px',
            color: theme.colors.inputPlaceholder,
          },
        },
      },
    }),
    [theme]
  );

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [attachmentPreviews, setAttachmentPreviews] = useState<{ file: File; url: string }[]>([]);

  useEffect(() => {
    const previews = attachments.map((file) => ({
      file,
      url: URL.createObjectURL(file),
    }));
    setAttachmentPreviews(previews);
    return () => {
      previews.forEach(({ url }) => URL.revokeObjectURL(url));
    };
  }, [attachments]);

  const workspaceOptions: Option[] = useMemo(() => {
    const opts = workspaces
      .filter((ws) => !!ws?.name)
      .map((ws) => ({
        id: ws.name,
        label: ws.workgroup_name ? `${ws.name} (${ws.workgroup_name})` : ws.name,
      }));
    // de-dupe by id (name) to avoid Select warnings
    const seen = new Set<string>();
    return opts.filter((o) => {
      const id = String(o.id);
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  }, [workspaces]);

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<SupportFormValues>({
    defaultValues: {
      category: '',
      workspace: [],
      subject: '',
      description: '',
    },
  });

  useEffect(() => {
    if (!isShow) return;
    reset();
    setAttachments([]);
  }, [isShow, reset]);

  const { mutateAsync, isLoading } = useMutationSupport();

  const onSubmit: SubmitHandler<SupportFormValues> = async (data) => {
    try {
      const images = attachments.length > 0 ? await Promise.all(attachments.map(fileToBase64Payload)) : undefined;
      const payload: SupportRequestPayload = {
        org_id: currentOrg?.org_id,
        email_id: userInfo?.email,
        category: data.category,
        workspace: data.workspace?.length ? data.workspace : undefined,
        subject: data.subject,
        description: data.description,
        images,
      };

      const res = await mutateAsync(payload);

      if (res?.Result?.redirectUrl) {
        const redirectUrl = res.Result.redirectUrl;
        if (!openZendeskSupportInNewTab(redirectUrl)) {
          showToast({
            kind: 'negative',
            message: 'Received an invalid support redirect URL.',
          });
          return;
        }
        showToast({
          kind: 'info',
          message: res.Result.message || res.Message || 'Please use Zendesk for support requests.',
        });
        onClose();
        return;
      }

      showToast({
        kind: 'positive',
        message: res?.Message || 'Support request submitted successfully',
      });
      onClose();
    } catch (e) {
      showToast({
        kind: 'negative',
        message: getErrorMessage(e as AxiosError),
      });
    }
  };

  const addAttachments = (files: File[]) => {
    const onlyImages = files.filter((f) => f.type.startsWith('image/'));
    if (onlyImages.length !== files.length) {
      showToast({ kind: 'warning', message: 'Only image attachments are supported (PNG/JPG/WebP/GIF).' });
    }

    const oversize = onlyImages.filter((f) => f.size > MAX_SUPPORT_IMAGE_BYTES);
    if (oversize.length > 0) {
      showToast({
        kind: 'warning',
        message:
          oversize.length === 1
            ? `${oversize[0].name} exceeds the ${MAX_SUPPORT_IMAGE_MB}MB size limit.`
            : `${oversize.length} files exceed the ${MAX_SUPPORT_IMAGE_MB}MB size limit.`,
      });
    }

    const sizeOk = onlyImages.filter((f) => f.size <= MAX_SUPPORT_IMAGE_BYTES);
    if (sizeOk.length === 0) return;

    let shouldWarnAttachmentLimit = false;
    setAttachments((prev) => {
      const newFiles = sizeOk.filter(
        (f) => !prev.find((p) => p.name === f.name && p.size === f.size && p.lastModified === f.lastModified)
      );
      const room = Math.max(0, MAX_SUPPORT_IMAGE_ATTACHMENTS - prev.length);
      const added = newFiles.slice(0, room);
      shouldWarnAttachmentLimit = newFiles.length > added.length;
      return [...prev, ...added];
    });

    if (shouldWarnAttachmentLimit) {
      showToast({
        kind: 'warning',
        message: `You can upload up to ${MAX_SUPPORT_IMAGE_ATTACHMENTS} images.`,
      });
    }
  };

  return (
    <Drawer isOpen={isShow} onClose={onClose} size="auto">
      <DrawerHeader>
        <div
          className={css({
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            width: '100%',
            fontWeight: 'normal',
          })}
        >
          <span className={css({ display: 'inline-flex', flexShrink: 0, color: theme.colors['text.primary'] })}>
            <SupportHeaderChatIcon />
          </span>
          <span
            className={css({
              fontSize: '18px',
              lineHeight: '24px',
              fontWeight: 600,
              color: theme.colors['text.primary'],
            })}
          >
            Support
          </span>
        </div>
      </DrawerHeader>

      <DrawerBody>
        <form>
          <FormSection>
            <div
              className={css({
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '12px',
                backgroundColor: theme.colors['background.secondary'],
                border: `1px solid ${theme.colors.divider}`,
                borderRadius: '8px',
                padding: '12px',
              })}
            >
              <div>
                <div
                  className={css({
                    fontSize: '12px',
                    lineHeight: '16px',
                    fontWeight: 600,
                    color: theme.colors['text.primary'],
                  })}
                >
                  Org ID
                </div>
                <div
                  className={css({
                    marginTop: '6px',
                    fontSize: '14px',
                    lineHeight: '20px',
                    fontWeight: 400,
                    color: theme.colors['text.primary'],
                  })}
                >
                  {currentOrg?.org_id || '-'}
                </div>
              </div>
              <div>
                <div
                  className={css({
                    fontSize: '12px',
                    lineHeight: '16px',
                    fontWeight: 600,
                    color: theme.colors['text.primary'],
                  })}
                >
                  Creator email ID
                </div>
                <div
                  className={css({
                    marginTop: '6px',
                    fontSize: '14px',
                    lineHeight: '20px',
                    fontWeight: 400,
                    color: theme.colors['text.primary'],
                  })}
                >
                  {userInfo?.email || '-'}
                </div>
              </div>
            </div>
          </FormSection>

          <FormSection>
            <div
              className={css({
                fontSize: '14px',
                lineHeight: '20px',
                fontWeight: 600,
                marginBottom: '8px',
                color: theme.colors['text.primary'],
              })}
            >
              Select Category <span className={css({ color: theme.colors.negative })}>*</span>
            </div>
            <Controller
              control={control}
              name="category"
              rules={{ required: 'This field is required.' }}
              render={({ field: { value, onChange, ref, ...field } }) => (
                <Select
                  size={FIELD_SIZE}
                  options={categoryOptions}
                  onChange={({ value }) => onChange(String(value?.[0]?.id || ''))}
                  value={value ? [{ id: value }] : []}
                  clearable={false}
                  inputRef={ref}
                  placeholder="Select category"
                  {...field}
                />
              )}
            />
            {errors.category && (
              <div className={css({ color: theme.colors.negative, marginTop: '8px', fontSize: '12px' })}>
                {errors.category.message}
              </div>
            )}
          </FormSection>

          <FormSection>
            <div
              className={css({
                fontSize: '14px',
                lineHeight: '20px',
                fontWeight: 600,
                marginBottom: '8px',
                color: theme.colors['text.primary'],
              })}
            >
              Select Workspace
            </div>
            <Controller
              control={control}
              name="workspace"
              render={({ field: { value, onChange, ref, ...field } }) => (
                <Select
                  size={FIELD_SIZE}
                  multi
                  options={workspaceOptions}
                  onChange={({ value: v }) => onChange((v || []).map((o) => String(o.id)))}
                  value={(value || []).map((v) => ({ id: v }))}
                  clearable
                  inputRef={ref}
                  placeholder="Select workspaces (optional)"
                  {...field}
                />
              )}
            />
          </FormSection>

          <FormSection>
            <div
              className={css({
                fontSize: '14px',
                lineHeight: '20px',
                fontWeight: 600,
                marginBottom: '8px',
                color: theme.colors['text.primary'],
              })}
            >
              Subject <span className={css({ color: theme.colors.negative })}>*</span>
            </div>
            <Controller
              control={control}
              name="subject"
              rules={{ required: 'This field is required.' }}
              render={({ field: { ref, ...field } }) => (
                <Input
                  {...field}
                  inputRef={ref}
                  size={FIELD_SIZE}
                  placeholder="Enter subject"
                  autoComplete="off"
                  overrides={alignWithSelectControlOverrides}
                />
              )}
            />
            {errors.subject && (
              <div className={css({ color: theme.colors.negative, marginTop: '8px', fontSize: '12px' })}>
                {errors.subject.message}
              </div>
            )}
          </FormSection>

          <FormSection>
            <div className={css({ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' })}>
              <div
                className={css({
                  fontSize: '14px',
                  lineHeight: '20px',
                  fontWeight: 600,
                  color: theme.colors['text.primary'],
                })}
              >
                Description <span className={css({ color: theme.colors.negative })}>*</span>
              </div>
              <StatefulToolTipsPopover
                triggerType={TRIGGER_TYPE.hover}
                accessibilityType={ACCESSIBILITY_TYPE.tooltip}
                ignoreBoundary={true}
                placement={PLACEMENT.bottomLeft}
                showArrow={false}
                content={
                  <div className={css({ fontSize: '12px', maxWidth: '320px' })}>
                    <div className={css({ fontWeight: 600, marginBottom: '6px' })}>Tip:</div>
                    <ul
                      className={css({
                        marginTop: '4px',
                        marginRight: 0,
                        marginBottom: 0,
                        marginLeft: 0,
                        paddingLeft: '20px',
                        listStyleType: 'disc',
                        listStylePosition: 'outside',
                      })}
                    >
                      <li className={css({ display: 'list-item', marginBottom: '4px' })}>Steps to reproduce</li>
                      <li className={css({ display: 'list-item', marginBottom: '4px' })}>
                        Exact error message (copy/paste preferred)
                      </li>
                      <li className={css({ display: 'list-item', marginBottom: '4px' })}>
                        Expected behavior vs actual behavior
                      </li>
                      <li className={css({ display: 'list-item' })}>What changed recently (if applicable)</li>
                    </ul>
                  </div>
                }
              >
                <span
                  className={css({
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '18px',
                    height: '18px',
                    borderRadius: '999px',
                    border: `1px solid ${theme.colors['text.link']}`,
                    color: theme.colors['text.link'],
                    fontSize: '12px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    userSelect: 'none',
                  })}
                  aria-label="Description field tips"
                >
                  ?
                </span>
              </StatefulToolTipsPopover>
            </div>

            <Controller
              control={control}
              name="description"
              rules={{ required: 'This field is required.' }}
              render={({ field: { ref, ...field } }) => (
                <Textarea
                  {...field}
                  inputRef={ref}
                  autoComplete="off"
                  size={FIELD_SIZE}
                  placeholder="Please explain the issue in detail..."
                  rows={4}
                  overrides={alignWithSelectControlOverrides}
                />
              )}
            />
            {errors.description && (
              <div className={css({ color: theme.colors.negative, marginTop: '8px', fontSize: '12px' })}>
                {errors.description.message}
              </div>
            )}
          </FormSection>

          <FormSection>
            <div
              className={css({
                fontSize: '16px',
                lineHeight: '22px',
                fontWeight: 400,
                marginBottom: '10px',
                color: theme.colors['text.primary'],
              })}
            >
              Attach Images
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              className={css({ display: 'none' })}
              onChange={(e) => {
                const files = Array.from(e.target.files || []);
                addAttachments(files);
                // allow re-selecting the same file
                e.currentTarget.value = '';
              }}
            />

            <button
              type="button"
              className={css({
                width: '100%',
                borderRadius: '8px',
                border: `1px dashed ${theme.colors.divider}`,
                backgroundColor: theme.colors['background.primary'],
                paddingTop: '20px',
                paddingBottom: '20px',
                paddingLeft: '16px',
                paddingRight: '16px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                cursor: 'pointer',
              })}
              onClick={() => fileInputRef.current?.click()}
            >
              <span className={css({ display: 'inline-flex', flexShrink: 0 })}>
                <SupportUploadIllustrationIcon />
              </span>
              <span
                className={css({
                  fontSize: '14px',
                  lineHeight: '20px',
                  fontWeight: 400,
                  color: theme.colors['text.secondary'],
                })}
              >
                Click to upload images
              </span>
              <span
                className={css({
                  fontSize: '12px',
                  lineHeight: '16px',
                  fontWeight: 400,
                  color: theme.colors['text.secondary'],
                })}
              >
                PNG, JPG
              </span>
            </button>

            {attachmentPreviews.length > 0 ? (
              <div className={css({ marginTop: '10px', display: 'flex', flexWrap: 'wrap', gap: '8px' })}>
                {attachmentPreviews.map(({ file, url }) => (
                  <div
                    key={`${file.name}-${file.lastModified}-${file.size}`}
                    className={css({
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      border: `1px solid ${theme.colors.divider}`,
                      borderRadius: '8px',
                      padding: '6px 8px',
                      maxWidth: '100%',
                    })}
                  >
                    <img
                      alt={file.name}
                      src={url}
                      className={css({
                        width: '28px',
                        height: '28px',
                        borderRadius: '4px',
                        objectFit: 'cover',
                        flexShrink: 0,
                      })}
                    />
                    <div
                      className={css({
                        fontSize: '12px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      })}
                    >
                      {file.name}
                    </div>
                    <button
                      type="button"
                      aria-label={`Remove ${file.name}`}
                      className={css({
                        marginLeft: 'auto',
                        border: 'none',
                        background: 'transparent',
                        cursor: 'pointer',
                        color: theme.colors['text.secondary'],
                        display: 'inline-flex',
                        alignItems: 'center',
                      })}
                      onClick={() => setAttachments((prev) => prev.filter((p) => p !== file))}
                    >
                      <MdClose size={18} />
                    </button>
                  </div>
                ))}
              </div>
            ) : null}
          </FormSection>
        </form>
      </DrawerBody>

      <DrawerAction>
        <Button
          kind="secondary"
          size="large"
          onClick={onClose}
          overrides={{ BaseButton: { style: { marginRight: '16px' } } }}
        >
          Cancel
        </Button>
        <Button isLoading={isLoading} size="large" onClick={handleSubmit(onSubmit)}>
          Submit
        </Button>
      </DrawerAction>
    </Drawer>
  );
}
